import { z } from "zod";
import type { FormQuestion, FormPage } from "@/components/features/FormBuilder/types/form.types";
import {
  isQuestionVisible,
  isQuestionRequired,
  evaluateGroupEntry,
  type EvalResult,
} from "@/components/features/FormBuilder/utils/evaluateLogic";
import { OTHER_TEXT_SUFFIX } from "@/components/features/FormBuilder/utils/formManagerAnswers";
import { isBlankAnswer } from "@/components/features/FormBuilder/utils/isBlankAnswer";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s().-]{7,20}$/;

function shapeForQuestion(q: FormQuestion): z.ZodTypeAny {
  switch (q.type) {
    case "checkbox":
    case "dropdown":
      if (q.type === "checkbox" || (q.type === "dropdown" && q.allowMultiple)) {
        return z.array(z.string()).optional();
      }
      return z.any().optional();
    default:
      return z.any().optional();
  }
}

function validateQuestionAnswer(
  q: FormQuestion,
  required: boolean,
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
) {
  const answer = data[q.id];
  const empty = isBlankAnswer(answer, q.type);

  if (required && empty) {
    ctx.addIssue({
      code: "custom",
      message: "This field is required",
      path: [q.id],
    });
    return;
  }

  if (q.allowOther) {
    const otherSelected =
      answer === "__other__" || (Array.isArray(answer) && answer.includes("__other__"));
    const otherValue = data[`${q.id}__other`];
    if (otherSelected && !otherValue?.toString().trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Please specify your answer",
        path: [`${q.id}__other`],
      });
    }
  }

  if (!empty) {
    if (q.type === "email" && typeof answer === "string" && !EMAIL_REGEX.test(answer.trim())) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter a valid email address",
        path: [q.id],
      });
    }
    if (q.type === "phone" && typeof answer === "string" && !PHONE_REGEX.test(answer.trim())) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter a valid phone number",
        path: [q.id],
      });
    }
    if (
      q.type === "number" &&
      typeof answer === "string" &&
      answer.trim() !== "" &&
      isNaN(Number(answer))
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter a valid number",
        path: [q.id],
      });
    }
  }
}

export function buildAnswerSchema(
  visibleQuestions: { q: FormQuestion; required: boolean }[],
): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const { q } of visibleQuestions) {
    shape[q.id] = q.type === "repeating-group" ? z.array(z.any()).optional() : shapeForQuestion(q);
  }

  return z
    .object(shape)
    .passthrough()
    .superRefine((data, ctx) => {
      for (const { q, required } of visibleQuestions) {
        if (q.type === "repeating-group") {
          const items = Array.isArray(data[q.id]) ? (data[q.id] as unknown[]) : [];
          const itemLabel = q.groupConfig?.itemLabel || "item";

          if (required && items.length === 0) {
            ctx.addIssue({
              code: "custom",
              message: `Add at least one ${itemLabel}`,
              path: [q.id],
            });
            continue;
          }

          if (q.groupConfig?.minItems !== undefined && items.length < q.groupConfig.minItems) {
            ctx.addIssue({
              code: "custom",
              message: `Add at least ${q.groupConfig.minItems} ${itemLabel}(s)`,
              path: [q.id],
            });
          }

          if (q.groupConfig?.maxItems !== undefined && items.length > q.groupConfig.maxItems) {
            ctx.addIssue({
              code: "custom",
              message: `Add at most ${q.groupConfig.maxItems} ${itemLabel}(s)`,
              path: [q.id],
            });
          }

          continue;
        }

        validateQuestionAnswer(q, required, data, ctx);
      }
    });
}

export function buildGroupEntrySchema(
  group: FormQuestion,
  item: Record<string, unknown>,
  context?: FormLogicContext,
): z.ZodType<Record<string, unknown>> {
  const { visibleFields, requiredIds } = evaluateGroupEntry(group, item, context);
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of visibleFields) {
    shape[field.id] = shapeForQuestion(field);
  }

  return z
    .object(shape)
    .passthrough()
    .superRefine((data, ctx) => {
      for (const field of visibleFields) {
        const required = field.required || requiredIds.has(field.id);
        validateQuestionAnswer(field, required, data, ctx);
      }
    });
}

export function getVisibleQuestions(page: FormPage | undefined, evalResult: EvalResult) {
  if (!page) return [];
  const result: { q: FormQuestion; required: boolean }[] = [];
  for (const section of page.sections) {
    if (evalResult.hiddenSections.has(section.id)) continue;
    for (const q of section.questions) {
      if (!isQuestionVisible(q.id, q, evalResult)) continue;
      result.push({ q, required: isQuestionRequired(q, evalResult) });
    }
  }
  return result;
}

export function scrollToFirstError(errors: Record<string, unknown>) {
  const firstKey = Object.keys(errors)[0];
  if (!firstKey) return;
  const questionId = firstKey.endsWith(OTHER_TEXT_SUFFIX)
    ? firstKey.slice(0, -OTHER_TEXT_SUFFIX.length)
    : firstKey;
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-question-id="${CSS.escape(questionId)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.querySelector<HTMLElement>("input, textarea, select, button")?.focus({
      preventScroll: true,
    });
  });
}
